<template>
  <div class="ad-management">
    <div class="page-header">
      <h2>广告列表</h2>
      <el-button type="primary" @click="showCreateDialog">发布广告</el-button>
    </div>

    <!-- 搜索表单 -->
    <el-form :inline="true" :model="searchForm" class="search-form">
      <el-form-item label="广告标题">
        <el-input v-model="searchForm.title" placeholder="请输入广告标题" clearable @keyup.enter.native="handleSearch" />
      </el-form-item>
      <el-form-item label="广告类型">
        <el-select v-model="searchForm.ad_type" placeholder="请选择广告类型" clearable>
          <el-option
            v-for="item in adTypes"
            :key="item.type"
            :label="typeOptionLabel(item)"
            :value="item.type"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="发布人">
        <el-input v-model="searchForm.admin_user_name" placeholder="请输入发布人" clearable @keyup.enter.native="handleSearch" />
      </el-form-item>
      <el-form-item label="是否置顶">
        <el-select v-model="searchForm.is_top" placeholder="请选择" clearable>
          <el-option label="置顶" :value="1" />
          <el-option label="普通" :value="0" />
        </el-select>
      </el-form-item>
      <el-form-item label="上架状态">
        <el-select v-model="searchForm.status" placeholder="请选择" clearable>
          <el-option label="上架" :value="1" />
          <el-option label="下架" :value="0" />
        </el-select>
      </el-form-item>
      <el-form-item label="有效期">
        <el-select v-model="searchForm.expire_status" placeholder="请选择" clearable>
          <el-option label="有效" value="valid" />
          <el-option label="已过期" value="expired" />
          <el-option label="永久" value="permanent" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 数据表格 -->
    <el-table v-loading="loading" :data="adList" border style="width: 100%">
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column prop="title" label="广告标题" min-width="200" show-overflow-tooltip />
      <el-table-column prop="ad_type_name" label="类型" width="100">
        <template slot-scope="scope">
          <el-tag :type="getAdTypeTagType(scope.row.ad_type)" size="small">
            {{ scope.row.ad_type_name }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="contact_name" label="联系人" width="90" />
      <el-table-column prop="contact_phone" label="联系人手机号" width="130" />
      <el-table-column prop="expire_time_desc" label="有效期" width="160">
        <template slot-scope="scope">
          <el-tag :type="getExpireTagType(scope.row.expire_status)" size="mini">
            {{ scope.row.expire_status_name }}
          </el-tag>
          <span style="margin-left: 6px; font-size: 12px; color: #909399;">
            {{ scope.row.expire_time_desc }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="is_top" label="置顶" width="80">
        <template slot-scope="scope">
          <el-tag :type="scope.row.is_top == 1 ? 'danger' : 'info'" size="mini">
            {{ scope.row.is_top_name }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="is_free" label="非会员免费" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.is_free == 1 ? 'success' : 'info'" size="mini">
            {{ scope.row.is_free_name }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="80">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status == 1 ? 'success' : 'info'" size="mini">
            {{ scope.row.status_name }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="view_count" label="浏览" width="70" />
      <el-table-column prop="message_count" label="留言" width="70">
        <template slot-scope="scope">
          <el-link
            v-if="scope.row.message_count > 0"
            type="primary"
            @click="handleView(scope.row)"
          >{{ scope.row.message_count }}</el-link>
          <span v-else>0</span>
        </template>
      </el-table-column>
      <el-table-column prop="admin_user_name" label="发布人" width="100" />
      <el-table-column prop="create_time" label="创建时间" width="160" />
      <el-table-column label="操作" width="160" fixed="right">
        <template slot-scope="scope">
          <div class="table-operations">
            <el-button size="mini" type="text" @click="handleView(scope.row)">查看</el-button>
            <el-button size="mini" type="text" class="edit-btn" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button
              size="mini"
              type="text"
              :class="scope.row.status == 1 ? 'offline-btn' : 'online-btn'"
              @click="handleToggleStatus(scope.row)"
            >{{ scope.row.status == 1 ? '下架' : '上架' }}</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.limit"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 发布/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="720px">
      <el-form ref="adForm" :model="adForm" :rules="adRules" label-width="110px">
        <el-form-item label="广告标题" prop="title">
          <el-input v-model="adForm.title" placeholder="请输入广告标题" maxlength="200" show-word-limit />
        </el-form-item>
        <el-form-item label="广告类型" prop="ad_type">
          <el-select v-model="adForm.ad_type" placeholder="请选择广告类型" style="width: 100%">
            <el-option
              v-for="item in adTypes"
              :key="item.type"
              :label="typeOptionLabel(item)"
              :value="item.type"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="广告详情" prop="content">
          <el-input
            v-model="adForm.content"
            type="textarea"
            :rows="6"
            placeholder="请输入广告详情"
          />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="联系人姓名" prop="contact_name">
              <el-input v-model="adForm.contact_name" placeholder="请输入联系人姓名" maxlength="50" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系人手机号" prop="contact_phone">
              <el-input v-model="adForm.contact_phone" placeholder="请输入联系人手机号" maxlength="11" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="有效期">
              <el-date-picker
                v-model="adForm.expire_time"
                type="datetime"
                placeholder="不填表示永久有效"
                style="width: 100%"
                value-format="yyyy-MM-dd HH:mm:ss"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="是否置顶">
              <el-switch
                v-model="adForm.is_top"
                :active-value="1"
                :inactive-value="0"
                active-text="置顶"
                inactive-text="普通"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="非会员免费">
          <el-switch
            v-model="adForm.is_free"
            :active-value="1"
            :inactive-value="0"
            active-text="开启"
            inactive-text="关闭"
          />
          <div class="form-tip">
            开启后，非会员也能直接看到完整手机号，且不需要付费解锁；关闭则非会员需要付费解锁（会员始终免费）。
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 查看详情对话框 -->
    <el-dialog title="广告详情" :visible.sync="viewDialogVisible" width="900px">
      <div v-if="currentAd" class="ad-detail">
        <div class="detail-section">
          <div class="section-title">基本信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">广告ID</div>
              <div class="detail-value">{{ currentAd.id }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">广告标题</div>
              <div class="detail-value">{{ currentAd.title }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">广告类型</div>
              <div class="detail-value">
                <el-tag :type="getAdTypeTagType(currentAd.ad_type)">{{ currentAd.ad_type_name }}</el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">广告详情</div>
              <div class="detail-value detail-pre">{{ currentAd.content }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">联系人</div>
              <div class="detail-value">{{ currentAd.contact_name }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">联系人手机号</div>
              <div class="detail-value">{{ currentAd.contact_phone }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">有效期</div>
              <div class="detail-value">
                <el-tag :type="getExpireTagType(currentAd.expire_status)" size="mini">
                  {{ currentAd.expire_status_name }}
                </el-tag>
                <span style="margin-left: 6px;">{{ currentAd.expire_time_desc }}</span>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">是否置顶</div>
              <div class="detail-value">{{ currentAd.is_top_name }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">非会员免费</div>
              <div class="detail-value">
                <el-tag :type="currentAd.is_free == 1 ? 'success' : 'info'" size="mini">
                  {{ currentAd.is_free_name }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">上架状态</div>
              <div class="detail-value">
                <el-tag :type="currentAd.status == 1 ? 'success' : 'info'" size="mini">
                  {{ currentAd.status_name }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">浏览次数</div>
              <div class="detail-value">{{ currentAd.view_count || 0 }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">发布人</div>
              <div class="detail-value">{{ currentAd.admin_user_name || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">创建时间</div>
              <div class="detail-value">{{ currentAd.create_time }}</div>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <div class="section-title">用户留言（{{ currentAd.message_count || 0 }}）</div>
          <el-table :data="currentAd.messages || []" border size="mini">
            <el-table-column prop="user_name" label="留言人" width="100" />
            <el-table-column prop="user_mobile" label="手机号" width="130" />
            <el-table-column prop="content" label="留言内容" min-width="220" show-overflow-tooltip />
            <el-table-column prop="access_type_name" label="来源" width="90" />
            <el-table-column prop="is_block_name" label="状态" width="80">
              <template slot-scope="scope">
                <el-tag :type="scope.row.is_block == 1 ? 'danger' : 'success'" size="mini">
                  {{ scope.row.is_block_name }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="amount" label="金额" width="80">
              <template slot-scope="scope">¥{{ scope.row.amount || 0 }}</template>
            </el-table-column>
            <el-table-column prop="create_time" label="留言时间" width="160" />
          </el-table>
          <div v-if="!(currentAd.messages || []).length" class="empty-tip">暂无留言</div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="viewDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="handleEditFromDetail">编辑</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getAdList, getAdDetail, getAdTypes, createAd, updateAd, changeAdStatus } from '@/api/ad'

export default {
  name: 'AdList',
  data() {
    return {
      loading: false,
      submitting: false,
      adList: [],
      adTypes: [],
      searchForm: {
        title: '',
        ad_type: '',
        admin_user_name: '',
        is_top: '',
        status: '',
        expire_status: ''
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      dialogVisible: false,
      viewDialogVisible: false,
      dialogTitle: '发布广告',
      currentAd: null,
      adForm: this.getEmptyForm(),
      adRules: {
        title: [
          { required: true, message: '请输入广告标题', trigger: 'blur' },
          { max: 200, message: '标题不能超过200个字符', trigger: 'blur' }
        ],
        ad_type: [
          { required: true, message: '请选择广告类型', trigger: 'change' }
        ],
        content: [
          { required: true, message: '请输入广告详情', trigger: 'blur' }
        ],
        contact_name: [
          { required: true, message: '请输入联系人姓名', trigger: 'blur' }
        ],
        contact_phone: [
          { required: true, message: '请输入联系人手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
        ]
      }
    }
  },
  mounted() {
    // 从「广告类型」页面点广告数跳过来时带上类型筛选
    if (this.$route.query.ad_type) {
      this.searchForm.ad_type = Number(this.$route.query.ad_type)
    }
    this.fetchAdTypes()
    this.fetchAdList()
  },
  methods: {
    getEmptyForm() {
      return {
        id: null,
        title: '',
        ad_type: this.defaultTypeId(),
        content: '',
        contact_name: '',
        contact_phone: '',
        expire_time: '',
        is_top: 0,
        is_free: 0
      }
    },
    // 类型下拉里的显示文案，停用的类型标出来
    typeOptionLabel(item) {
      const icon = item.icon || '📎'
      const suffix = item.status === 1 ? '' : '（已停用）'
      return `${icon} ${item.name}${suffix}`
    },
    // 默认选第一个启用的类型
    // 注意：data() 里初始化 adForm 时 adTypes 还没赋值，这里要做空判断
    defaultTypeId() {
      const list = this.adTypes || []
      const enabled = list.find(item => item.status === 1)
      if (enabled) {
        return enabled.type
      }
      return list.length ? list[0].type : ''
    },
    async fetchAdTypes() {
      try {
        const response = await getAdTypes()
        this.adTypes = response.data || []
      } catch (error) {
        console.error('获取广告类型失败:', error)
      }
    },
    async fetchAdList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          ...this.searchForm
        }
        const response = await getAdList(params)
        this.adList = response.data.list || []
        this.pagination.total = response.data.total || 0
      } catch (error) {
        console.error('获取广告列表失败:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.fetchAdList()
    },
    handleReset() {
      this.searchForm = {
        title: '',
        ad_type: '',
        admin_user_name: '',
        is_top: '',
        status: '',
        expire_status: ''
      }
      this.pagination.page = 1
      this.fetchAdList()
    },
    handleSizeChange(size) {
      this.pagination.limit = size
      this.fetchAdList()
    },
    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchAdList()
    },
    showCreateDialog() {
      this.dialogTitle = '发布广告'
      this.adForm = this.getEmptyForm()
      this.dialogVisible = true
    },
    async handleView(row) {
      try {
        const response = await getAdDetail(row.id)
        this.currentAd = response.data
        this.viewDialogVisible = true
      } catch (error) {
        console.error('获取广告详情失败:', error)
      }
    },
    async handleEdit(row) {
      try {
        const response = await getAdDetail(row.id)
        const detail = response.data
        this.adForm = {
          id: detail.id,
          title: detail.title,
          ad_type: detail.ad_type,
          content: detail.content,
          contact_name: detail.contact_name,
          contact_phone: detail.contact_phone,
          expire_time: detail.expire_time || '',
          is_top: detail.is_top,
          is_free: Number(detail.is_free) === 1 ? 1 : 0
        }
        this.dialogTitle = '编辑广告'
        this.dialogVisible = true
      } catch (error) {
        console.error('获取广告详情失败:', error)
      }
    },
    handleToggleStatus(row) {
      const isOnline = Number(row.status) === 1
      const tip = isOnline
        ? '确认下架该广告吗？下架后用户端不再显示！'
        : '确认上架该广告吗？上架后用户端立即可见！'
      this.$confirm(tip, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await changeAdStatus(row.id, isOnline ? 0 : 1)
          this.$message.success(isOnline ? '已下架' : '已上架')
          this.fetchAdList()
        } catch (error) {
          console.error('上架/下架失败:', error)
        }
      })
    },
    handleSubmit() {
      this.$refs.adForm.validate(async (valid) => {
        if (!valid) {
          return
        }
        this.submitting = true
        try {
          if (this.adForm.id) {
            await updateAd(this.adForm)
            this.$message.success('更新成功')
          } else {
            await createAd(this.adForm)
            this.$message.success('发布成功')
          }
          this.dialogVisible = false
          this.fetchAdList()
        } catch (error) {
          console.error('操作失败:', error)
        } finally {
          this.submitting = false
        }
      })
    },
    handleEditFromDetail() {
      this.viewDialogVisible = false
      this.handleEdit(this.currentAd)
    },
    getAdTypeTagType(type) {
      const map = { 1: 'primary', 2: 'success', 3: 'warning', 4: 'info', 5: '', 6: 'info' }
      if (map[type] !== undefined) {
        return map[type]
      }
      // 后台新增的类型没有预设颜色，按 id 轮着取
      const palette = ['primary', 'success', 'warning', 'info', 'danger']
      return palette[(Number(type) - 1) % palette.length]
    },
    getExpireTagType(status) {
      const map = { valid: 'success', expired: 'info', permanent: '' }
      return map[status] || ''
    }
  }
}
</script>

<style scoped>
.ad-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #303133;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.table-operations {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: nowrap;
}

.table-operations .el-button--text {
  padding: 2px 8px;
  font-size: 12px;
  border: none;
  background: none;
}

.table-operations .edit-btn {
  color: #409eff;
}

.table-operations .edit-btn:hover {
  color: #66b1ff;
  background-color: #ecf5ff;
}

.table-operations .online-btn {
  color: #67c23a;
}

.table-operations .online-btn:hover {
  color: #85ce61;
  background-color: #f0f9eb;
}

.table-operations .offline-btn {
  color: #e6a23c;
}

.table-operations .offline-btn:hover {
  color: #ebb563;
  background-color: #fdf6ec;
}

.dialog-footer {
  text-align: right;
}

.form-tip {
  color: #909399;
  font-size: 12px;
  line-height: 18px;
  margin-top: 4px;
}

.ad-detail {
  padding: 10px 0;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 4px solid #409eff;
  line-height: 20px;
}

.detail-table {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
  background-color: #fff;
}

.detail-row {
  display: flex;
  border-bottom: 1px solid #ebeef5;
  min-height: 50px;
  align-items: center;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 500;
  color: #606266;
  width: 150px;
  text-align: right;
  padding: 15px 20px;
  background-color: #fafafa;
  border-right: 1px solid #ebeef5;
  flex-shrink: 0;
  box-sizing: border-box;
}

.detail-value {
  color: #303133;
  word-break: break-word;
  padding: 15px 20px;
  flex: 1;
  box-sizing: border-box;
}

.detail-pre {
  white-space: pre-wrap;
  line-height: 1.7;
}

.empty-tip {
  text-align: center;
  color: #909399;
  font-size: 13px;
  padding: 20px 0;
}
</style>
